import { PlanterGameElements } from "./PlanterMiniGameTypes";

/**
 * Renderer for the Planter Mini Game component
 */
export class PlanterMiniGameRenderer {
    private readonly shadowRoot: ShadowRoot;
    private readonly styles: string;
    private readonly onWaterButtonClick: () => void;
    private readonly onContinueButtonClick: () => void;
    private elements: PlanterGameElements = {
        waterLevelElement: null,
        waterLevelTextElement: null,
        plantElement: null,
        successMessageElement: null,
        continueButtonElement: null,
    };

    /**
     * Constructor for the renderer
     * @param shadowRoot The shadow root to render into
     * @param styles The CSS styles to apply
     * @param onWaterButtonClick Callback for water button click
     * @param onContinueButtonClick Callback for continue button click
     */
    public constructor(
        shadowRoot: ShadowRoot,
        styles: string,
        onWaterButtonClick: () => void,
        onContinueButtonClick: () => void
    ) {
        this.shadowRoot = shadowRoot;
        this.styles = styles;
        this.onWaterButtonClick = onWaterButtonClick;
        this.onContinueButtonClick = onContinueButtonClick;
    }

    /**
     * Render the component
     * @param waterLevel Current water level (0-100)
     * @param isCompleted Whether the mini-game is completed
     * @returns Elements created during rendering
     */
    public render(waterLevel: number, isCompleted: boolean): PlanterGameElements {
        this.shadowRoot.innerHTML = "";

        const style: HTMLStyleElement = document.createElement("style");
        style.textContent = this.styles;
        this.shadowRoot.appendChild(style);

        const container: HTMLDivElement = document.createElement("div");
        container.className = "game-container";

        // Title and description
        const title: HTMLHeadingElement = document.createElement("h2");
        title.className = "title";
        title.textContent = "Plant Watering Mini-Game";
        container.appendChild(title);

        const description: HTMLParagraphElement = document.createElement("p");
        description.className = "description";
        description.textContent = "Water the plant to soften the soil and reveal what's hidden underneath.";
        container.appendChild(description);

        // Water meter
        const waterLevelContainer: HTMLDivElement = document.createElement("div");
        waterLevelContainer.className = "water-level-container";

        const waterLevel_: HTMLDivElement = document.createElement("div");
        waterLevel_.className = "water-level";
        waterLevel_.style.width = isCompleted ? "100%" : `${waterLevel}%`;

        const waterLevelText: HTMLDivElement = document.createElement("div");
        waterLevelText.className = "water-level-text";
        waterLevelText.textContent = isCompleted ? "100%" : `${waterLevel}%`;

        waterLevelContainer.appendChild(waterLevel_);
        waterLevelContainer.appendChild(waterLevelText);
        container.appendChild(waterLevelContainer);

        // Plant
        const plantContainer: HTMLDivElement = document.createElement("div");
        plantContainer.className = "plant-container";

        const plant: HTMLDivElement = document.createElement("div");
        plant.className = "plant";
        if (isCompleted || waterLevel >= 50) {
            plant.classList.add("watered");
        }

        plantContainer.appendChild(plant);
        container.appendChild(plantContainer);

        if (!isCompleted) {
            // Watering can
            const wateringCanContainer: HTMLDivElement = document.createElement("div");
            wateringCanContainer.className = "watering-can-container";

            const wateringCan: HTMLDivElement = document.createElement("div");
            wateringCan.className = "watering-can";
            wateringCan.addEventListener("click", this.onWaterButtonClick);

            wateringCanContainer.appendChild(wateringCan);
            container.appendChild(wateringCanContainer);

            // Water button
            const waterButton: HTMLButtonElement = document.createElement("button");
            waterButton.className = "button water-button";
            waterButton.textContent = "Water Plant";
            waterButton.addEventListener("click", this.onWaterButtonClick);
            container.appendChild(waterButton);
        }
        else {
            // Continue button for completed state
            const continueButton: HTMLButtonElement = document.createElement("button");
            continueButton.className = "button continue-button";
            continueButton.textContent = "Continue";
            continueButton.addEventListener("click", this.onContinueButtonClick);
            container.appendChild(continueButton);
            this.elements.continueButtonElement = continueButton;
        }

        // Success message (initially hidden)
        const successMessage: HTMLDivElement = document.createElement("div");
        successMessage.className = "success-message";
        if (isCompleted) {
            successMessage.textContent = "The plant has been watered properly. You can now examine it to find what's buried in the soil.";
            successMessage.style.display = "block";
        }
        container.appendChild(successMessage);

        this.shadowRoot.appendChild(container);

        // Store references to elements
        this.elements = {
            waterLevelElement: waterLevel_,
            waterLevelTextElement: waterLevelText,
            plantElement: plant,
            successMessageElement: successMessage,
            continueButtonElement: this.elements.continueButtonElement,
        };

        return this.elements;
    }

    /**
     * Update the water level display
     * @param waterLevel Current water level (0-100)
     */
    public updateWaterLevel(waterLevel: number): void {
        if (this.elements.waterLevelElement) {
            this.elements.waterLevelElement.style.width = `${waterLevel}%`;
        }

        if (this.elements.waterLevelTextElement) {
            this.elements.waterLevelTextElement.textContent = `${waterLevel}%`;
        }

        if (this.elements.plantElement && waterLevel >= 50) {
            this.elements.plantElement.classList.add("watered");
        }
    }

    /**
     * Show the success message
     * @param message Success message text
     */
    public showSuccessMessage(message: string): void {
        if (this.elements.successMessageElement) {
            this.elements.successMessageElement.textContent = message;
            this.elements.successMessageElement.style.display = "block";
        }
    }
}
